import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ShareButton from "@/components/ShareButton";
import { HeartHandshake, ExternalLink, ArrowUpRight } from "lucide-react";

function formatCurrency(amount, currency) {
  try {
    return new Intl.NumberFormat("en-EU", { style: "currency", currency: currency || "EUR", maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount.toFixed(0)} ${currency || "EUR"}`;
  }
}

export default function Fundraiser() {
  const { t } = useLang();
  const [data, setData] = useState({ totals: {}, entries: [] });
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/fundraiser").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const totals = Object.entries(data.totals || {});
  const primary = totals.find(([c]) => c === "EUR") || totals[0];

  return (
    <div data-testid="fundraiser-page" className="container-refined py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow"><HeartHandshake className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> {t("fundraiser.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{t("fundraiser.title")}</h1>
        <p className="mt-6 text-forest-700 leading-relaxed">{t("fundraiser.lede")}</p>
      </div>

      <section className="mt-12 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-warm !p-10 relative overflow-hidden" data-testid="fundraiser-total-card">
          <p className="eyebrow">{t("fundraiser.total_sent")}</p>
          <p className="mt-6 font-serif text-6xl md:text-7xl text-forest-900">
            {primary ? formatCurrency(primary[1], primary[0]) : formatCurrency(0, "EUR")}
          </p>
          {totals.length > 1 && (
            <p className="mt-3 text-sm text-forest-700">
              {totals.filter(([c]) => c !== (primary && primary[0])).map(([c, a]) => `${formatCurrency(a, c)}`).join("  ·  ")}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book" className="btn-primary" data-testid="fundraiser-cta-book">
              {t("fundraiser.cta_book")} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
            <ShareButton variant="secondary" />
          </div>
          <div aria-hidden className="pointer-events-none absolute -right-10 -bottom-10 h-56 w-56 rounded-full bg-wheat-500/15 blur-2xl" />
        </div>
        <div className="card-warm">
          <p className="eyebrow">Direct support</p>
          <h3 className="mt-3 font-serif text-2xl text-forest-900">Dzyga&apos;s Paw</h3>
          <p className="mt-3 text-sm text-forest-700 leading-relaxed">
            You can also give directly. Every session fee I receive is transferred to Ukraine and to Dzyga&apos;s Paw.
          </p>
          <a href="https://dzygaspaw.com/en" target="_blank" rel="noreferrer" className="btn-secondary mt-6" data-testid="fundraiser-dzyga">
            dzygaspaw.com <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
          </a>
        </div>
      </section>

      <section className="mt-16" data-testid="fundraiser-entries">
        <p className="eyebrow">{t("fundraiser.proof")}</p>
        {loading && <p className="mt-6 text-forest-700">{t("common.loading")}</p>}
        {!loading && (data.entries || []).length === 0 && (
          <div className="mt-6 card-warm max-w-3xl" data-testid="fundraiser-empty">
            <p className="font-serif text-2xl md:text-3xl text-forest-900 leading-snug italic">{t("fundraiser.no_entries")}</p>
            <Link to="/book" className="btn-primary mt-8" data-testid="fundraiser-empty-cta">
              {t("nav.book")} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        )}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(data.entries || []).map((e) => (
            <article key={e.id} className="card-warm !p-0 overflow-hidden group" data-testid={`fund-entry-${e.id}`}>
              {e.proof_image ? (
                <button
                  type="button"
                  onClick={() => setPreview(e.proof_image)}
                  className="block w-full h-56 overflow-hidden"
                  data-testid={`fund-proof-open-${e.id}`}
                >
                  <img src={e.proof_image} alt={`Transfer ${e.reference || e.id}`} className="h-full w-full object-cover transition group-hover:scale-105" />
                </button>
              ) : (
                <div className="h-24 w-full bg-gradient-to-br from-wheat-100 to-surface flex items-center justify-center text-xs text-forest-700 uppercase tracking-widest">
                  {t("fundraiser.proof")}
                </div>
              )}
              <div className="p-6">
                <p className="font-serif text-3xl text-forest-900">{formatCurrency(e.amount, e.currency)}</p>
                {e.description && <p className="mt-1 text-sm text-forest-700">{e.description}</p>}
                <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] uppercase tracking-widest text-forest-700/70">
                  <div>
                    <p className="text-forest-700/60">{t("fundraiser.received")}</p>
                    <p className="text-forest-800 normal-case tracking-normal text-sm">{(e.transferred_at || e.received_at || "").slice(0, 10)}</p>
                  </div>
                  <div>
                    <p className="text-forest-700/60">{t("fundraiser.source")}</p>
                    <p className="text-forest-800 normal-case tracking-normal text-sm">{e.source}</p>
                  </div>
                </div>
                {e.reference && (
                  <p className="mt-3 text-[11px] uppercase tracking-widest text-wheat-700">
                    {t("fundraiser.reference")}: <span className="text-forest-800 normal-case tracking-normal">{e.reference}</span>
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {preview && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="fund-proof-modal"
          onClick={() => setPreview(null)}
          className="fixed inset-0 z-50 bg-forest-900/80 backdrop-blur-md flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img src={preview} alt="Transfer proof" className="max-h-[90vh] max-w-[95vw] rounded-2xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}
